import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box, Tooltip} from "@mui/material";
import SearchAutocomplete from "../../SearchAutocomplete";

const defaultMapValue = (option) => option?.id ?? "";

// Espera tras la última pulsación antes de lanzar la búsqueda en el servidor.
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Search field backed by SearchAutocomplete.
 *
 * Option sources (via the field config):
 *  - `options`: static array of {id, label}.
 *  - `loadOptions(term)`: async loader returning [{id, label}].
 *
 * Filtering (`filterMode`, default "client"):
 *  - "client": `loadOptions("")` is called once on mount to fetch the full list,
 *    then filtered client-side by the lookup term.
 *  - "server": `loadOptions(term)` is called on mount ("") and again on each
 *    search with the current input, replacing the options (no client filter).
 *
 * Searches run automatically while the user types, once they pause for
 * SEARCH_DEBOUNCE_MS, so this field hides the search icon. Enter still
 * runs the search immediately.
 *
 * `tooltip` (optional) explains the filter on hover.
 *
 * Emits the mapped value (by default `option.id`) upwards through `onChange`.
 */
const AutocompleteField = ({config, value, onChange, size}) => {
  const mapValue = config.mapValue ?? defaultMapValue;
  const serverSide = config.filterMode === "server";
  const freeSolo = config.freeSolo === true;

  const [allOptions, setAllOptions] = useState(config.options ?? []);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [inputValue, setInputValue] = useState(() => (config.mapValue && value ? String(value) : ""));
  const [lookupTerm, setLookupTerm] = useState("");

  const loadOptionsRef = useRef(config.loadOptions);
  useEffect(() => {
    loadOptionsRef.current = config.loadOptions;
  }, [config.loadOptions]);

  // Solo cuenta la última petición: así una respuesta lenta no pisa a una más reciente.
  const requestIdRef = useRef(0);
  const debounceRef = useRef(null);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const fetchOptions = useCallback((term) => {
    if (!loadOptionsRef.current) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    Promise.resolve(loadOptionsRef.current(term ?? ""))
      .then((opts) => {
        if (requestId === requestIdRef.current) setAllOptions(Array.isArray(opts) ? opts : []);
      })
      .catch(() => {
        if (requestId === requestIdRef.current) setAllOptions([]);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, []);

  const runSearch = (term) => {
    if (serverSide) fetchOptions(term);
    else setLookupTerm(term);
  };

  const scheduleSearch = (term) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(term), SEARCH_DEBOUNCE_MS);
  };

  // Fetch the initial option list once on mount. `loadOptions` identity may be
  // unstable across renders, so we read it from a ref and keep deps empty to
  // avoid a re-fetch loop.
  useEffect(() => {
    if (loadOptionsRef.current) fetchOptions("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync static options if they change.
  useEffect(() => {
    if (config.options) setAllOptions(config.options);
  }, [config.options]);

  // Clear local selection when the value is reset from the parent.
  const firstValueCheckRef = useRef(true);
  useEffect(() => {
    if (firstValueCheckRef.current) {
      firstValueCheckRef.current = false;
      return;
    }

    if (value === undefined || value === null || value === "") {
      clearTimeout(debounceRef.current);
      setSelected(null);
      setInputValue("");
      setLookupTerm("");
      // Reload initial options when cleared
      if (loadOptionsRef.current) {
        fetchOptions("");
      }
    }
  }, [value, fetchOptions]);

  const options = useMemo(() => {
    if (serverSide) return allOptions;
    const term = lookupTerm.trim().toLowerCase();
    if (!term) return allOptions;
    return allOptions.filter((opt) => (opt.label || "").toLowerCase().includes(term));
  }, [serverSide, allOptions, lookupTerm]);

  const handleSearch = () => {
    clearTimeout(debounceRef.current);
    runSearch(inputValue.trim());
  };

  const handleInputChange = (_, v, reason) => {
    // "reset" lo dispara MUI al montar y al elegir una opción: el texto lo gestionamos nosotros.
    if (reason === "reset") return;
    setInputValue(v);
    if (reason !== "input" && reason !== "clear") return;

    // Con texto libre lo escrito ya es el valor del filtro: no hace falta elegir una opción.
    if (freeSolo) {
      onChange(v.trim());
      // Al vaciarlo, el efecto de `value` se encarga de recargar las opciones iniciales.
      if (!v.trim()) return;
    }

    scheduleSearch(v.trim());
  };

  const autocomplete = (
    <SearchAutocomplete
      label={config.label}
      options={options}
      loading={loading}
      value={selected}
      inputValue={inputValue}
      size={size}
      width={config.width ?? 220}
      freeSolo={freeSolo}
      // Sin lupa: la consulta se lanza sola al dejar de escribir.
      showSearchIcon={false}
      onInputChange={handleInputChange}
      onChange={(_, option) => {
        // Con `freeSolo`, MUI entrega un string cuando el usuario valida lo que ha escrito.
        const typed = typeof option === "string";
        setSelected(typed ? null : option);
        setInputValue(typed ? option : (option?.label ?? ""));
        if (typed) onChange(option.trim());
        else onChange(option ? mapValue(option) : "");
      }}
      onSearch={handleSearch}
    />
  );

  if (!config.tooltip) return autocomplete;

  return (
    <Tooltip title={config.tooltip} disableInteractive>
      <Box component="span" sx={{display: "inline-flex"}}>
        {autocomplete}
      </Box>
    </Tooltip>
  );
};

export default AutocompleteField;
